// scheduling/components/IDDTimeline.jsx
// Stacked bar chart timeline for IDD Review — jobs stack within weekly columns.
// Shows job density at a glance. Sits above the table on the same page.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getJobPhase, PHASE_LABELS } from '../utils/schedConstants';

// ── Constants ────────────────────────────────────────────────────────────────

const WEEK_W = 100;        // Width per week column
const BLOCK_H = 22;        // Height per stacked block
const BLOCK_GAP = 4;       // Gap between stacked blocks (bumped from 2 → 4 for clearer lane separation)
const BLOCK_PAD = 4;       // Padding top/bottom inside week column
const HEADER_H = 44;       // Month + week header
const MONTH_H = 20;
const WEEK_H = 24;

// Default window when no cycle cutoff is set: today through next FY end (April 30, ≥12wk out).
function calcDefaultWindow() {
  const today = new Date();
  let fyEnd = new Date(today.getFullYear(), 3, 30);
  if ((fyEnd - today) / (7 * 86400000) < 12) {
    fyEnd = new Date(today.getFullYear() + 1, 3, 30);
  }
  return { weeksAhead: Math.ceil((fyEnd - today) / (7 * 86400000)) };
}

const HOUR_BUCKETS = [
  { max: 10,       color: '#D9D9D9', label: '0–10h'   },
  { max: 20,       color: '#C7E8A5', label: '11–20h'  },
  { max: 30,       color: '#A1D86D', label: '21–30h'  },
  { max: 50,       color: '#6CC24A', label: '31–50h'  },
  { max: 100,      color: '#639F6A', label: '51–100h' },
  { max: Infinity, color: '#4B796E', label: '101+h'   },
];

function getBucketColor(hours) {
  if (hours == null || hours <= 0) return '#EF4444';
  for (const b of HOUR_BUCKETS) if (hours <= b.max) return b.color;
  return '#4B796E';
}

function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 160;
}

// ── Date helpers ─────────────────────────────────────────────────────────────

function getMonday(d) {
  const dt = new Date(d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}

// Parse a 'YYYY-MM-DD' string as LOCAL midnight (not UTC).
// Plain `new Date('2026-05-25')` parses as UTC midnight, which in CST shifts
// back to Sun May 24 19:00, breaking week-of and getMonday calculations.
// For AI suggestion strings + any other date-only field, use this helper.
function parseLocalDate(val) {
  if (!val) return null;
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const d = new Date(val);
  return isNaN(d) ? null : d;
}

function addWeeks(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n * 7);
  return dt;
}

function weeksBetween(a, b) {
  return Math.round((b - a) / (7 * 86400000));
}

function fmtMonthYear(d) {
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
function fmtMonthShort(d) {
  return d.toLocaleDateString('en-US', { month: 'short' });
}

function fmtWeekLabel(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDate(val) {
  if (!val) return '';
  const d = parseLocalDate(val);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// ── Component ────────────────────────────────────────────────────────────────

// hoveredJob / onHoverJob (2026-10-19): hovering a table row highlights its bar, and hovering a
// bar highlights the row (the page owns the hovered job). Since 2026-10-21 one bar is gold:
// the hovered job's, else the selected one's.
export default function IDDTimeline({ jobs, edits, getEffective, selectedJob, onSelectJob, aiSuggestions = {}, zoom = 1, onZoomChange, externalScrollRef, onHorizontalScroll, cutoffDate = null, windowMode = 'current', onWindowModeChange, hoveredJob = null, onHoverJob }) {
  const [colorKeyOpen, setColorKeyOpen] = useState(false);
  const internalScrollRef = useRef(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const blockRefs = useRef({});
  const [tooltip, setTooltip] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const setZoom = onZoomChange; // delegate to parent
  const weekW = Math.round(WEEK_W * zoom);
  const blockH = Math.round(BLOCK_H * zoom);
  const blockGap = Math.round(BLOCK_GAP * zoom);
  const blockPad = Math.round(BLOCK_PAD * zoom);

  // The highlighted bar (2026-10-21): the hovered job, else the selected one, in gold.
  const focusJob = hoveredJob ?? selectedJob;
  // A focus job whose bar is scrolled out of view is brought in after a short pause, so
  // hovering a table row always shows its bar (sweeping across rows doesn't scroll).
  useEffect(() => {
    if (!focusJob) return undefined;
    const timer = setTimeout(() => {
      const el = blockRefs.current[focusJob];
      const c = scrollRef.current;
      if (!el || !c || !c.scrollTo) return;
      const left = el.offsetLeft, top = el.offsetTop;
      const inView = left + el.offsetWidth > c.scrollLeft && left < c.scrollLeft + c.clientWidth
        && top + el.offsetHeight > c.scrollTop && top < c.scrollTop + c.clientHeight;
      if (inView) return;
      c.scrollTo({
        left: left + el.offsetWidth / 2 - c.clientWidth / 2,
        top: top + el.offsetHeight / 2 - c.clientHeight / 2,
        behavior: 'smooth',
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [focusJob]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll timeline to center the selected job's block
  useEffect(() => {
    if (!selectedJob || !scrollRef.current) return;
    const blockEl = blockRefs.current[selectedJob];
    if (!blockEl) return;
    const container = scrollRef.current;
    const blockLeft = blockEl.offsetLeft;
    const blockWidth = blockEl.offsetWidth;
    const blockTop = blockEl.offsetTop;
    const blockHeight = blockEl.offsetHeight;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    container.scrollTo({
      left: blockLeft + blockWidth / 2 - containerWidth / 2,
      top: blockTop + blockHeight / 2 - containerHeight / 2,
      behavior: 'smooth',
    });
  }, [selectedJob]);

  // Window selection:
  //  - 'current' (default): today's Monday → cutoff (or fyEnd if no cutoff).
  //    Focuses on the actionable window for the cycle.
  //  - 'year': (cutoff − 52 weeks) → cutoff, captures one full annual cycle of
  //    PY-shifted context. Useful for comparing CY plan against last year's actuals.
  const { originMonday, weeksAhead, cutoffWeekIdx } = useMemo(() => {
    const todayMon = getMonday(new Date());
    if (cutoffDate) {
      const cutoffMon = getMonday(parseLocalDate(cutoffDate));
      if (windowMode === 'year') {
        const origin = addWeeks(cutoffMon, -52);
        return { originMonday: origin, weeksAhead: 53, cutoffWeekIdx: 52 };
      }
      // 'current' mode: origin = today's Monday, ends at cutoff.
      const wks = Math.max(4, Math.floor((cutoffMon - todayMon) / (7 * 86400000)) + 1);
      return { originMonday: todayMon, weeksAhead: wks, cutoffWeekIdx: wks - 1 };
    }
    return {
      originMonday: todayMon,
      weeksAhead: calcDefaultWindow().weeksAhead,
      cutoffWeekIdx: null,
    };
  }, [cutoffDate, windowMode]);

  // ── Stage 22G: auto fit-to-zoom on windowMode toggle ──────────────────
  // When the user flips between 'current' (today→cutoff) and 'year'
  // (cutoff−52→cutoff), weeksAhead changes substantially. Without this,
  // the existing zoom either crams everything into a sliver (year mode at
  // current-week zoom) or stretches the current-week range edge-to-edge
  // (current mode at year zoom). Run the same math as the manual Fit
  // button (right above), but only on actual mode changes — manual zoom
  // adjustments after a fit are preserved until the next toggle.
  const lastFitModeRef = useRef(null);
  useEffect(() => {
    if (lastFitModeRef.current === windowMode) return;
    if (!scrollRef.current || !setZoom) return;
    const containerW = scrollRef.current.clientWidth;
    if (!containerW || !weeksAhead) return;
    const fit = containerW / (weeksAhead * WEEK_W);
    setZoom(Math.round(fit * 100) / 100);
    lastFitModeRef.current = windowMode;
  }, [windowMode, weeksAhead, scrollRef, setZoom]);

  // ── Build weeks array ────────────────────────────────────────────────

  const weeks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < weeksAhead; i++) {
      arr.push({ monday: addWeeks(originMonday, i), idx: i });
    }
    return arr;
  }, [originMonday, weeksAhead]);

  const totalW = weeksAhead * weekW;

  // ── Month spans for header ───────────────────────────────────────────

  const months = useMemo(() => {
    const result = [];
    let cur = null;
    for (const w of weeks) {
      const key = `${w.monday.getFullYear()}-${w.monday.getMonth()}`;
      if (!cur || cur.key !== key) {
        if (cur) result.push(cur);
        cur = {
          key,
          label: fmtMonthYear(w.monday),
          shortLabel: fmtMonthShort(w.monday),
          monday: w.monday,
          x: w.idx * weekW,
          w: weekW,
        };
      } else {
        cur.w += weekW;
      }
    }
    if (cur) result.push(cur);
    // Use the short "Mon" label when the cell is narrower than ~60px so it fits
    // without truncating ugly. Full label still surfaces in the title attribute.
    for (const m of result) m.displayLabel = m.w < 60 ? m.shortLabel : m.label;
    return result;
  }, [weeks, weekW]);

  // ── Map jobs into per-week stacks ────────────────────────────────────

  const { blocks, maxTrack } = useMemo(() => {
    // Build block info per job
    const blockInfos = [];
    for (const job of jobs) {
      const pe = job.PEJobIndex;
      const startRaw = getEffective(job, 'startDeadline');
      const endRaw = getEffective(job, 'deadline');
      const remaining = getEffective(job, 'remainingHrs') ?? job.RemainingHours;

      const start = startRaw ? getMonday(parseLocalDate(startRaw) || new Date(startRaw)) : null;
      const end = endRaw ? getMonday(parseLocalDate(endRaw) || new Date(endRaw)) : null;
      if (!start || !end) continue;

      let sIdx = weeksBetween(originMonday, start);
      let eIdx = weeksBetween(originMonday, end);
      // Data anomaly: some PE jobs have IDD start > IDD end (e.g. start Jul 31,
      // end Apr 30). That gave sIdx > eIdx, spanWeeks negative, the track loop
      // never reserved any weeks, and the block silently took track 0 + auto-width
      // overlapping its neighbors. Collapse the visual to a 1-week block at the
      // saved start position so the block still shows + the track logic works.
      // Also flag it so the row gets a "fix this" warning in the table.
      const inverted = sIdx > eIdx;
      if (inverted) eIdx = sIdx;
      if (eIdx < 0 || sIdx >= weeksAhead) continue; // entirely off-screen
      sIdx = Math.max(sIdx, 0);
      eIdx = Math.min(eIdx, weeksAhead - 1);

      const color = getBucketColor(remaining);
      const phase = getJobPhase(job.StatusDetail);
      const isDirty = !!edits[pe];

      const spanWeeks = Math.max(1, eIdx - sIdx + 1);
      blockInfos.push({ job, pe, sIdx, eIdx, color, phase, isDirty, remaining, spanWeeks, isAI: false, inverted });
    }

    // Add AI suggestion ghost blocks (only if not yet accepted into edits)
    for (const job of jobs) {
      const pe = job.PEJobIndex;
      const rec = aiSuggestions[pe];
      if (!rec || !rec.startdate || !rec.enddate) continue;
      // Check if already accepted (edit matches AI)
      const e = edits[pe] || {};
      if (e.startDeadline === rec.startdate && e.deadline === rec.enddate) continue;

      const aiStart = getMonday(parseLocalDate(rec.startdate));
      const aiEnd = getMonday(parseLocalDate(rec.enddate));
      let sIdx = weeksBetween(originMonday, aiStart);
      let eIdx = weeksBetween(originMonday, aiEnd);
      if (eIdx < 0 || sIdx >= weeksAhead) continue;
      sIdx = Math.max(sIdx, 0);
      eIdx = Math.min(eIdx, weeksAhead - 1);
      const spanWeeks = eIdx - sIdx + 1;

      blockInfos.push({
        job, pe, sIdx, eIdx, spanWeeks, isAI: true,
        color: '#7C3AED', phase: getJobPhase(job.StatusDetail),
        isDirty: false, remaining: Number(getEffective(job, 'remainingHrs') ?? job.RemainingHours) || 0,
        aiReason: rec.Optimization_Logic,
      });
    }

    // Assign each block a track (y-slot) using a greedy interval-scheduling approach.
    // Tiebreakers (in order):
    //   1. Earlier start week first.
    //   2. Real blocks before AI ghosts in the same week (so existing IDD jobs claim
    //      tracks first; AI suggestions stack below them).
    //   3. Longer spans first (visual stability — long bars don't get squeezed).
    //   4. Stable by PE so React keys don't reorder between renders.
    blockInfos.sort((a, b) =>
      a.sIdx - b.sIdx
      || ((a.isAI ? 1 : 0) - (b.isAI ? 1 : 0))
      || b.spanWeeks - a.spanWeeks
      || a.pe - b.pe
    );

    // tracks[week] = Set of occupied track indices
    const weekTracks = new Array(weeksAhead).fill(null).map(() => new Set());
    let maxTrack = 0;

    for (const b of blockInfos) {
      // Find the lowest track that's free across all weeks this block spans
      let track = 0;
      while (true) {
        let free = true;
        for (let w = b.sIdx; w <= b.eIdx; w++) {
          if (weekTracks[w].has(track)) { free = false; break; }
        }
        if (free) break;
        track++;
      }
      b.track = track;
      if (track > maxTrack) maxTrack = track;
      // Reserve this track for all weeks the block spans
      for (let w = b.sIdx; w <= b.eIdx; w++) weekTracks[w].add(track);
    }

    // Dev-mode sanity check: warn if any week has two blocks on the same track.
    // Helps diagnose render-vs-algorithm if overlap is reported again.
    if (process.env.NODE_ENV !== 'production') {
      for (let w = 0; w < weeksAhead; w++) {
        const seen = new Set();
        for (const b of blockInfos) {
          if (b.sIdx <= w && w <= b.eIdx) {
            if (seen.has(b.track)) {
              // eslint-disable-next-line no-console
              console.warn('[IDDTimeline] Track collision at week', w, 'track', b.track, b);
            }
            seen.add(b.track);
          }
        }
      }
    }

    return { blocks: blockInfos, maxTrack };
  }, [jobs, edits, getEffective, originMonday, weeksAhead, aiSuggestions]);

  const MAX_TIMELINE_H = 300; // cap timeline body height
  const rawBodyH = Math.max((maxTrack + 1) * (blockH + blockGap) + blockPad * 2, 50);
  const bodyH = rawBodyH; // actual content height for positioning
  const displayH = Math.min(rawBodyH, MAX_TIMELINE_H); // capped display height

  // ── Today marker ─────────────────────────────────────────────────────

  const todayX = useMemo(() => {
    const diff = (new Date() - originMonday) / 86400000;
    return (diff / 7) * weekW;
  }, [originMonday, weekW]);

  // ── Tooltip ──────────────────────────────────────────────────────────

  const handleBlockHover = useCallback((e, block) => {
    const j = block.job;
    const rec = aiSuggestions[block.pe];
    // For AI ghost blocks, show the AI-suggested dates; otherwise use effective dates
    const startDate = block.isAI && rec ? rec.startdate : getEffective(j, 'startDeadline');
    const endDate = block.isAI && rec ? rec.enddate : getEffective(j, 'deadline');
    setTooltip({
      x: e.clientX + 12,
      y: e.clientY + 12,
      content: [
        `${j.ClientCode} — ${j.ClientName}`,
        j.JobName,
        block.isAI ? 'AI Suggestion' : null,
        `Phase: ${PHASE_LABELS[block.phase] || j.StatusDetail || 'Unknown'}`,
        `Start: ${fmtDate(startDate)}`,
        `IDD End: ${fmtDate(endDate)}`,
        `Remaining: ${block.remaining != null ? block.remaining + 'h' : '\u2014'}`,
        `CY WIP: ${j.CYActualHours || 0}h`,
        // The decided budget (step 1), not PE's (it read 111h after "Use last year" set 141.5h).
        `Budget: ${Number(getEffective(j, 'budgetHours') ?? j.BudgetHours) || 0}h`,
        block.isAI && block.aiReason ? `Reason: ${block.aiReason}` : null,
        !block.isAI && j.PYDeadline ? `PY IDD: ${fmtDate(j.PYDeadline)}` : null,
        !block.isAI && j.PYCompletion ? `Approx. PY Completion: ${fmtDate(j.PYCompletion)}` : null,
      ].filter(Boolean),
    });
  }, [getEffective, aiSuggestions]);

  const handleBlockLeave = useCallback(() => setTooltip(null), []);

  // ── Render ────────────────────────────────────────────────────────────

  const contentH = HEADER_H + displayH + 16; // +16 for horizontal scrollbar

  return (
    <div className="idd-tl-root">
      <div
        className="idd-tl-collapsible"
        style={{ maxHeight: collapsed ? 0 : contentH, opacity: collapsed ? 0 : 1 }}
      >
        <div className="idd-tl-scroll" ref={scrollRef} onScroll={onHorizontalScroll} style={rawBodyH > MAX_TIMELINE_H ? { height: contentH } : undefined}>
        {/* Header: month + week labels */}
        <div className="idd-tl-header" style={{ width: totalW, height: HEADER_H }}>
          <div className="idd-tl-month-row" style={{ height: MONTH_H }}>
            {months.map(m => (
              <div key={m.key} className="idd-tl-month-cell" style={{ left: m.x, width: m.w }} title={m.label}>
                {m.displayLabel}
              </div>
            ))}
          </div>
          <div className="idd-tl-week-row" style={{ height: WEEK_H, top: MONTH_H }}>
            {weeks.map(w => (
              <div key={w.idx} className="idd-tl-week-cell" style={{ left: w.idx * weekW, width: weekW }}>
                {fmtWeekLabel(w.monday)}
              </div>
            ))}
          </div>
        </div>

        {/* Body: stacked blocks */}
        <div className="idd-tl-body" style={{ width: totalW, height: bodyH }}>
          {/* Vertical grid lines */}
          {weeks.map(w => (
            <div key={w.idx} className="idd-tl-gridline" style={{ left: w.idx * weekW, height: bodyH }} />
          ))}

          {/* Today marker */}
          {todayX > 0 && todayX < totalW && (
            <div className="idd-tl-today" style={{ left: todayX, height: bodyH }} />
          )}

          {/* Cycle-end marker (only when cutoffDate is set) */}
          {cutoffWeekIdx != null && (
            <div
              className="idd-tl-cycle-end"
              style={{ left: cutoffWeekIdx * weekW + weekW, height: bodyH }}
              title="Cycle cutoff"
            />
          )}

          {/* Job blocks (single continuous div per job) */}
          {blocks.map(block => {
            const x = block.sIdx * weekW + 2;
            const w = block.spanWeeks * weekW - 4;
            const y = blockPad + block.track * (blockH + blockGap);

            return (
              <div
                key={block.isAI ? `ai-${block.pe}` : block.pe}
                ref={el => { blockRefs.current[block.pe] = el; }}
                className={`idd-tl-block${block.isAI ? ' idd-tl-block--ai' : ''}${block.isDirty ? ' idd-tl-block--dirty' : ''}${focusJob === block.pe ? ' idd-tl-block--selected' : ''}${block.inverted ? ' idd-tl-block--invalid' : ''}`}
                style={{
                  left: x,
                  top: y,
                  width: w,
                  height: blockH,
                  background: block.color,
                  color: isLightColor(block.color) ? '#374151' : '#fff',
                  borderRadius: 4,
                }}
                onClick={() => onSelectJob(prev => prev === block.pe ? null : block.pe)}
                onMouseEnter={e => { handleBlockHover(e, block); if (onHoverJob) onHoverJob(block.pe); }}
                onMouseMove={e => setTooltip(t => t ? { ...t, x: e.clientX + 12, y: e.clientY + 12 } : t)}
                onMouseLeave={() => { handleBlockLeave(); if (onHoverJob) onHoverJob(null); }}
              >
                <span className="idd-tl-block-text">
                  {block.spanWeeks >= 3
                    ? `${block.job.ClientCode} \u2014 ${block.job.JobName}`
                    : block.spanWeeks >= 2
                      ? `${block.job.ClientCode} ${(block.job.JobName || '').split(' ').slice(0, 3).join(' ')}`
                      : block.job.ClientCode}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* Toggle bar */}
      <div className="idd-tl-toolbar">
        {!collapsed && <div className="idd-tl-zoom-spacer" />}
        <button className="idd-tl-toggle" onClick={() => setCollapsed(c => !c)}>
          <span className={`idd-tl-chevron${collapsed ? ' idd-tl-chevron--down' : ''}`}>{'\u25B2'}</span>
          {collapsed ? 'Show Timeline' : 'Hide Timeline'}
        </button>
        {!collapsed && (
          <div className="idd-tl-toolbar-group">
            {/* Window-mode toggle (was its own row above timeline) */}
            {onWindowModeChange && cutoffDate && (
              <div className="idd-tl-window-toggle" role="tablist" aria-label="Chart window">
                <button
                  className={`idd-tl-window-btn${windowMode === 'current' ? ' idd-tl-window-btn--active' : ''}`}
                  onClick={() => onWindowModeChange('current')}
                  title="Show this week through cycle cutoff"
                >Current</button>
                <button
                  className={`idd-tl-window-btn${windowMode === 'year' ? ' idd-tl-window-btn--active' : ''}`}
                  onClick={() => onWindowModeChange('year')}
                  title="Show one full year ending at cycle cutoff (PY context)"
                >Year</button>
              </div>
            )}

            {/* Color key (was the 'REMAINING HOURS:' row above timeline). Hover
                or click to expand a popover showing the bucket colors. */}
            <div
              className="idd-tl-colorkey-wrap"
              onMouseEnter={() => setColorKeyOpen(true)}
              onMouseLeave={() => setColorKeyOpen(false)}
            >
              <button
                className="idd-tl-zoom-btn"
                style={{ width: 'auto', padding: '0 6px', fontSize: '9px' }}
                onClick={() => setColorKeyOpen(o => !o)}
                title="Show the timeline color legend"
              >Color key</button>
              {colorKeyOpen && (
                <div className="idd-tl-colorkey-popover" onMouseEnter={() => setColorKeyOpen(true)}>
                  <div className="idd-tl-colorkey-label">Remaining Hours:</div>
                  <div className="idd-tl-colorkey-chips">
                    {HOUR_BUCKETS.map(b => (
                      <span
                        key={b.label}
                        className="idd-tl-colorkey-chip"
                        style={{ background: b.color, color: b.max <= 10 ? '#374151' : '#fff' }}
                      >{b.label}</span>
                    ))}
                    <span className="idd-tl-colorkey-chip" style={{ background: '#EF4444', color: '#fff' }}>Over Budget</span>
                    {Object.keys(aiSuggestions).length > 0 && (
                      <span className="idd-tl-colorkey-chip" style={{ background: '#7C3AED', color: '#fff', opacity: 0.6, border: '2px dashed #7C3AED' }}>AI Suggestion</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="idd-tl-zoom">
              <button className="idd-tl-zoom-btn" onClick={() => setZoom(z => Math.max(0.25, Math.floor((z - 0.01) * 4) / 4))} title="Zoom out">-</button>
              <button
                className="idd-tl-zoom-btn"
                style={{ width: 'auto', padding: '0 4px', fontSize: '9px' }}
                onClick={() => {
                  if (scrollRef.current) {
                    const fit = scrollRef.current.clientWidth / (weeksAhead * WEEK_W);
                    setZoom(Math.round(fit * 100) / 100);
                  }
                }}
                title="Zoom to fit"
              >Fit</button>
              <span className="idd-tl-zoom-label">{Math.round(zoom * 100)}%</span>
              <button className="idd-tl-zoom-btn" onClick={() => setZoom(z => Math.min(2, Math.ceil((z + 0.01) * 4) / 4))} title="Zoom in">+</button>
            </div>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="idd-tl-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.content.map((line, i) => (
            <div key={i} className={i === 0 ? 'idd-tl-tooltip-title' : ''}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}
